import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import {uniqBy} from 'lodash'
import { DropdownList, Multiselect } from "react-widgets";
import { toastr } from "react-redux-toastr";
import ErrorMessage from "../../../components/text/ErrorMessage";
import { Customer, CreateLead } from "../../../core/api/models";
import api from "../../../api";
import { City, CityLocation, CityZone, Company, useLead } from "../../../core";
import { useMasterData } from "../../../core/hooks/useMasterData";
import { TextInput } from "../../../components/form-group";
import { decodeHtmlEntities } from "../../../utils/common";

const LeadCreate: React.FunctionComponent<{
  error?: string;
  setStage: (stage: string) => void;
  leadCreated: (id?: string) => void;
  customerDetails?: Customer;
  companyDetails?: Company;
  contactIds?: string[];
}> = (props) => {
  const { createLead, updating, leadError } = useLead(api.lead);
  const masterData = useMasterData(api.settings);
  const [leadIntent, setLeadIntent] = React.useState<string>("");
  const [type, setType] = React.useState<string>("");
  const [cities, setCities] = useState<any>([]);
  const [microMarketsData, setMicroMarketsData] = useState<any>([]);
  const [preferredLocationsData, setPreferredLocationsData] = useState<any>([]);
  const [projectType, setProjectType] = useState<any>([]);
  const [propertyType, setPropertyType] = useState<any>([]);
  const [preferredDevelopers, setPreferredDevelopers] = useState<any>([]);
  const [floorPreferences, setFloorPreferences] = useState<any>([]);
  const [preferredProject, setPeferredProject] = useState<any>([]);

  const { control, handleSubmit, errors, watch, trigger, setValue } = useForm<CreateLead>({
    reValidateMode: "onChange",
    mode: "onSubmit",
    shouldFocusError: true,
  });

  const minArea = watch("minArea");
  const maxArea = watch("maxArea");
  const minPrice = watch("minPrice");
  const maxPrice = watch("maxPrice");

  useEffect(() => {
    trigger("maxPrice");
    trigger("minPrice");
    trigger("minArea");
    trigger("maxArea");
  }, [
    watch("minPrice"),
    watch("maxPrice"),
    watch("maxPrice"),
    watch("minArea"),
    watch("maxArea")
  ]);

  useEffect(() => {
    const fetchCityData = async () => {
      try {
        const cityData = await api.AddNewRequirement.loadAllCities();
        const cityOptions = cityData?.map((item: City) => ({
          value: item._id,
          name: decodeHtmlEntities(item?.cityName),
        }));
        cityOptions.sort((a, b) => a.name.localeCompare(b.name));
        setCities(cityOptions);
      } catch (error) {
        // console.log("Failed to fetch city data:", error);
      }

      try {
        const floorData = await api.AddNewRequirement.getPreferredFloor();
        const floorDataOptions = (floorData || [])
        .map((item: any) => ({
            value: item,
            name: item,
          }))
          .sort((a: { name: number }, b: { name: number }) => a.name - b.name);

        setFloorPreferences(floorDataOptions || []);
      } catch (error) {
        // console.log("Failed to fetch preferred floor:", error);
      }

      try {
        const developerData = await api.AddNewRequirement.getPreferredDevelopers(
          { search: "" }
        );
        const developerDataOptions = developerData?.map((item: any) => ({
          value: item._id,
          name: decodeHtmlEntities(item?.developer),
        }));
        setPreferredDevelopers(developerDataOptions);
      } catch (error) {
        // console.log("Failed to fetch preferred developers:", error);
      }

    };
    handlePropertyType();
    handleProjectType();

    fetchCityData();
  }, []);

  const handleCityZones = async (selectedCityId: string) => {
    try {
      const microMarketsListData = await api.AddNewRequirement.getCityZones(
        selectedCityId
      );
      const cityZoneOptions = microMarketsListData?.map((item: CityZone) => ({
        value: item._id,
        name: decodeHtmlEntities(item?.cityZoneName),
        cityLocations: item.cityLocations,
      }));
      setMicroMarketsData(cityZoneOptions);
      // Set micro markets data to the form field
      // setValue('microMarkets', []);
    } catch (error) {
      // console.log("Failed to fetch micro markets data:", error);
    }
  };

  const handlePreferredLocationsChange = async (selectedCityId: string) => {
    try {
      const preferredLocationsListData = await api.AddNewRequirement.getPreferedLocations(
        selectedCityId
      );
      const cityPreferedLocOptions = preferredLocationsListData?.map(
        (item: CityLocation) => ({
          value: item._id,
          name: decodeHtmlEntities(item?.cityLocName),
        })
      );
      setPreferredLocationsData(cityPreferedLocOptions);
    } catch (error) {
      // console.log("Failed to fetch preferred locations data:", error);
    }
  };
  const handlePreferredProjectChange = async (selectedCityId: string) => {
    try {
      const projectData = await api.AddNewRequirement.getPreferredProject({
        cityId: selectedCityId,
      });
      const projectDataOptions = projectData?.map((item: any) => ({
        value: item._id,
        name: decodeHtmlEntities(item?.projectName),
      }));
      setPeferredProject(projectDataOptions);
    } catch (error) {
      // console.log("Failed to fetch preferred project:", error);
    }
  };

  const handlePropertyType = async () => {
    try {
      const propertyTypeData = await api.AddNewRequirement.getPropertyType();
      const subTypes = propertyTypeData.reduce((acc: any, item: any) => {
        acc.push(
          ...item.subTypes.map(
            (subType: { _id: any; propertyTypeName: any }) => ({
              value: subType._id,
              name: decodeHtmlEntities(subType.propertyTypeName),
            })
          )
        );
        return acc;
      }, []);
      setPropertyType(subTypes);
    } catch (error) {
      // console.log("Failed to fetch property type data:", error);
    }
  };

  const handleProjectType = async () => {
    try {
      const projectTypeData = await api.AddNewRequirement.getProjectType();
      const subTypes = projectTypeData.reduce((acc: any, item: any) => {
        if (item?.subTypes) {
          acc.push(
            ...item.subTypes.map(
              (subType: { _id: any; propertyTypeName: any }) => ({
                value: subType._id,
                name: decodeHtmlEntities(subType.propertyTypeName),
              })
            )
          );
        }
        return acc;
      }, []);
      setProjectType(subTypes);
    } catch (error) {
      // console.log("Failed to fetch project type data:", error);
    }
  };


  const onSubmit = async (leadData?: any) => {
    const updateData: CreateLead = {
      leadType: type,
      leadSource: leadData?.leadSource?._id || leadData?.leadSource,
      leadIntent,
      customerId: props.customerDetails?._id,
      initialTouchPoint:
        leadData?.initialTouchPoint?._id || leadData?.initialTouchPoint,
      contactIds: props.contactIds,
      companyId: props.companyDetails?._id,
      decisionTimeframe: leadData?.decisionTimeframe?._id,
      title: leadData?.title,
      cities: leadData?.cities?.value || undefined,
      microMarkets: leadData?.microMarkets?.map((item: any) => item.value) || undefined,
      preferredLocations: leadData?.preferredLocations?.map(
        (item: any) => item?.value
      ) || undefined,
      propertyType: leadData?.propertyType?.value || undefined,
      projectType: leadData?.projectType?.map((pType: { value: any; }) => pType.value) || undefined,
      minPrice: leadData?.minPrice || undefined,
      maxPrice: leadData?.maxPrice || undefined,
      minArea: leadData?.minArea || undefined,
      maxArea: leadData?.maxArea || undefined,
      weightage: leadData?.weightage || undefined,
      floorPreferences:
        leadData?.floorPreferences?.map((item: any) => item?.value) || undefined,
      preferedProjects:
        leadData?.preferedProjects?.map((item: any) => item?.value) || undefined,
      preferredDevelopers:
        leadData?.preferredDevelopers?.map((item: any) => item?.value) || undefined,
      note: leadData?.note || undefined,
    };

    try {
      createLead(
        updateData,
        (id?: string) => {
          props.setStage("lead-edits");
          props.leadCreated(id);
        },
        (error) => {
          if (
            error?.response?.data?.data?.message &&
            Array.isArray(error?.response?.data?.data?.message)
          ) {
            toastr.error("Error", error?.response?.data?.data?.message.join());
          } else {
            toastr.error("Error", error?.response?.data?.message);
          }
        }
      );
    } catch (error) {
      if (
        error?.response?.data?.data?.message &&
        Array.isArray(error?.response?.data?.data?.message)
      ) {
        toastr.error("Error", error?.response?.data?.data?.message.join());
      } else {
        toastr.error("Error", error?.response?.data?.message);
      }
    }
  };

  const leadIntentSelect = (vaue: string) => {
    setLeadIntent(vaue);
    setType("");
  };

  const leadTypeSelect = (vaue: string) => {
    setType(vaue);
  };

  const leadType = masterData?.data?.leadTypes.filter(
    (x) => x.leadIntent?.code === leadIntent
  );

  const initialTouchPoints = masterData?.data?.initialTouchPoints;
  
  useEffect(() => {
    if(watch('microMarkets')?.length){
      const valuesToMatch  = watch('microMarkets')?.map((obj:any) => obj.value) || []
      
      const matchedValues: any = []
      microMarketsData.map((obj:any) =>{
        if(valuesToMatch.includes(obj.value)){
          matchedValues.push(...obj.cityLocations)
        }
      })
      
      const tempPreferredLocations = [...(watch('preferredLocations') || []) , ...matchedValues.map((item: {_id: string, cityLocName:string})=>({value: item._id, name: item.cityLocName}))]

      setValue("preferredLocations",uniqBy(tempPreferredLocations, 'value'))
    }
  }, [watch('microMarkets')])

  return (
    <div className="col-md-4 col-12 border-right ">
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="d-flex border-bottom align-items-center py-3 px-4">
          <h6 className="mb-0">Lead Details</h6>
        </div>
        <div className="p-4">

          <div className="form-group font-weight-semibold mb-4">
            <label htmlFor="" className="font-sm text-uppercase">
              Title<span className="text-danger">*</span>
            </label>
            <Controller
              control={control}
              rules={{ required: true }}
              name="title"
              // defaultValue={props.data?.name}
              render={({ onChange, value, name }) => (
                <input
                  type="text"
                  className="form-control rounded-sm font-weight-semibold"
                  placeholder="Title"
                  // name={name}
                  value={value}
                  onChange={onChange}
                // id="title"
                />
              )}
            />
            {errors.title && (
              <ErrorMessage>Title is required</ErrorMessage>
            )}
          </div>
          <div className="form-group font-weight-semibold mb-4">
            <label htmlFor="" className="font-sm text-uppercase">
              Lead Intent<span className="text-danger">*</span>
            </label>
            <Controller
              control={control}
              rules={{ required: true }}
              name="leadIntent"
              render={({ onChange, value }) => (
                <DropdownList
                  value={value}
                  defaultValue={value}
                  data={masterData?.data?.leadIntents}
                  textField={"name"}
                  valueField={"code"}
                  placeholder={"Select Lead Intent"}
                  onChange={(val) => {
                    leadIntentSelect(val.code);
                    onChange(val.code)
                  }}
                />
              )}
            />
            {errors.leadIntent && (
              <ErrorMessage>Lead Intent is required</ErrorMessage>
            )}
          </div>
          {/* <div className="form-group font-weight-semibold mb-4">
            <label htmlFor="" className="font-sm text-uppercase">
              Type<span className="text-danger">*</span>
            </label>
            <Controller
              control={control}
              rules={{ required: true }}
              name="leadType"
              render={({ onChange, value }) => (
                <DropdownList
                  value={type}
                  defaultValue={type}
                  data={leadType}
                  textField={"name"}
                  valueField={"code"}
                  placeholder={"Select Lead Type"}
                  onChange={(val) => {
                    leadTypeSelect(val.code);
                    onChange(val.code)
                  }}
                />
              )}
            />
            {errors.leadType && (
              <ErrorMessage>Lead Type is required</ErrorMessage>
            )}
          </div> */}
          <div className="form-group font-weight-semibold mb-4">
            <label htmlFor="" className="font-sm text-uppercase">
              Property Type Interested{!type ? <span className="text-danger">*</span>: null}
            </label>
            <Controller
              control={control}
              rules={{ required: !type }}
              name="propertyType"
              render={({ onChange, value }) => (
                <DropdownList
                  value={value}
                  defaultValue={value}
                  data={propertyType || []}
                  textField={"name"}
                  valueField={"value"}
                  placeholder={"Select Property Type Interested"}
                  onChange={onChange}
                />
              )}
            />
            {errors.propertyType && (
              <ErrorMessage>Property Type Interested is required</ErrorMessage>
            )}
          </div>
        

          {/* <div className="form-group font-weight-semibold mb-4">
            <label htmlFor="" className="font-sm text-uppercase">
              Initial Touch Point
            </label>
            <Controller
              control={control}
              rules={{ required: true }}
              name="initialTouchPoint"
              defaultValue={
                initialTouchPoints?.length === 1
                  ? initialTouchPoints?.[0]._id
                  : undefined
              }
              render={({ onChange, value }) => (
                <DropdownList
                  value={value}
                  defaultValue={value}
                  data={initialTouchPoints}
                  textField={"name"}
                  valueField={"_id"}
                  placeholder={"Select Initial Touch Point"}
                  onChange={onChange}
                />
              )}
            />
            {errors.initialTouchPoint && (
              <ErrorMessage>Initial Touch Point is required</ErrorMessage>
            )}
          </div> */}

          <div className="form-group font-weight-semibold mb-4">
            <label htmlFor="" className="font-sm text-uppercase">
              Decision Timeframe{!type ? <span className="text-danger">*</span>: null}
            </label>
            <Controller
              control={control}
              rules={{ required: !type }}
              name="decisionTimeframe"
              render={({ onChange, value }) => (
                <DropdownList
                  value={value}
                  defaultValue={value}
                  data={masterData?.data?.decisionTimeFrames || []}
                  textField={"name"}
                  valueField={"_id"}
                  placeholder={"Select Decision Timeframe"}
                  onChange={onChange}
                />
              )}
            />
            {errors.decisionTimeframe && (
              <ErrorMessage>Decision Timeframe is required</ErrorMessage>
            )}
          </div>

          {/* <div className="form-group font-weight-semibold mb-4">
            <label htmlFor="" className="font-sm text-uppercase">
              Lead Title
            </label>
            <Controller
              control={control}
              rules={{ required: true }}
              name="title"
              render={({ onChange, value, name }) => (
                <TextInput
                  gap="sm"
                  type="text"
                  placeholder="Enter title"
                  label=""
                  onChange={onChange}
                  name={name}
                  default={value}
                  className="form-control rounded-sm"
                />
              )}
            />
            {errors.title && <ErrorMessage>Title is required</ErrorMessage>}
          </div> */}

          <div className="form-group font-weight-semibold mb-4">
            <label className="font-sm font-weight-semibold mb-1" htmlFor="cities">
              CITY:<span className="text-danger">*</span>
            </label>
            <Controller
              name="cities"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: "City is required",
                },
              }}
              render={({ onChange, value }) => (
                <DropdownList
                  value={value}
                  data={cities}
                  busy={cities?.length === 0}
                  textField="name"
                  valueField={"value"}
                  filter="contains"
                  onChange={(val) => {
                    onChange(val);
                    // handleCityZones(val?.value);
                    // setValue("microMarkets", []);
                    handleCityZones(val?.value)
                    handlePreferredLocationsChange(val?.value);
                    handlePreferredProjectChange(val?.value);
                    setValue("preferredLocations", []);
                  }}
                  placeholder="Select City"
                />
              )}
            />
            {errors?.cities && <ErrorMessage>City is required</ErrorMessage>}
          </div>

          <div className="form-group font-weight-semibold mb-4">
            <label className="font-sm font-weight-semibold mb-1" htmlFor="microMarkets">
              MICRO MARKETS:
            </label>
            <Controller
              name="microMarkets"
              control={control}
              // rules={{
              //   required: "Micro Markets is required",
              //   validate: (value) => {
              //     return !!(value.length > 0);
              //   },
              // }}
              render={({ onChange, value }) => (
                <Multiselect
                  value={value}
                  data={microMarketsData}
                  textField={"name"}
                  valueField={"value"}
                  onChange={(val) => {
                    onChange(val);
                  }}
                  placeholder="Search"
                />
              )}
            />
            {/* {errors.microMarkets && (
            <ErrorMessage>Micro Markets is required</ErrorMessage>
          )} */}
          </div>

          <div className="form-group font-weight-semibold mb-4">
            <label
              className="font-sm font-weight-semibold mb-1"
              htmlFor="preferredLocations"
            >
              PREFERRED LOCATIONS:
            </label>
            <Controller
              name="preferredLocations"
              control={control}
              // rules={{
              //   required: {
              //     value: true,
              //     message: "Preferred Locations is required",
              //   },
              //   validate: (value) => {
              //     return !!(value.length > 0);
              //   },
              // }}
              render={({ onChange, value }) => (
                <Multiselect
                  value={value}
                  data={preferredLocationsData}
                  textField={"name"}
                  valueField={"value"}
                  onChange={(val) => {
                    onChange(val);
                  }}
                  placeholder="Search Locations"
                />
              )}
            />
            {errors.preferredLocations && (
              <ErrorMessage>Preferred Locations is required</ErrorMessage>
            )}
          </div>

          <div className="form-group font-weight-semibold mb-4">
            <label
              className="font-sm font-weight-semibold mb-1"
              htmlFor="projectType"
            >
              PROJECT TYPE:
            </label>
            <Controller
              name="projectType"
              control={control}
              // rules={{
              //   required: {
              //     value: true,
              //     message: "Project Type is required",
              //   },
              // }}
              // render={({ onChange, value }) => (
              //   <DropdownList
              //     value={value}
              //     data={projectType}
              //     textField={"name"}
              //     onChange={(val) => {
              //       onChange(val);
              //     }}
              //     placeholder="Select Project Type"
              //   />
              // )}

              render={({ onChange, value }) => (
                <Multiselect
                  value={value}
                  data={projectType}
                  textField={"name"}
                  valueField={"value"}
                  onChange={(val) => {
                    onChange(val);
                  }}
                  placeholder="Search Project Types"
                />
              )}
            />
            {/* {errors.projectType && (
            <ErrorMessage>{errors.projectType.message}</ErrorMessage>
          )} */}
          </div>

          <div className="form-group font-weight-semibold mb-1">
            <label
              className="font-md font-weight-semibold mb-1"
              htmlFor=""
            >
              Select Budget(Per Sq. Ft.)
            </label>
          </div>
          <div className="row gutter-8">
            <div className="col-6 form-group font-weight-semibold textInput-component-style mb-4">
              <label
                className="font-sm text-uppercase mb-1"
                htmlFor="minPrice"
              >
                Min Price:
              </label>
              <Controller
                name="minPrice"
                control={control}
                rules={{
                  validate: (value) => {
                    const numericMaxPrice = maxPrice;
                    if (
                      numericMaxPrice &&
                      (value === undefined || value === null || value === "")
                    ) {
                      return "Min Price is required";
                    }
                    return undefined;
                  },
                }}
                render={({ onChange, value, name }) => (
                  <TextInput
                    type="number"
                    placeholder="Min Price"
                    onChange={onChange}
                    name={name}
                    default={value}
                    gap="no"
                    clearFeildValue={true}
                  />
                )}
              />
              {errors.minPrice && (
                <ErrorMessage>{errors.minPrice.message}</ErrorMessage>
              )}
            </div>
              {/* <div className="col-auto d-flex align-items-center font-xxl text-gray-400 px-0 pt-2">
              -
            </div> */}
            <div className="col-6 form-group font-weight-semibold textInput-component-style mb-4">
              <label
                className="font-sm font-weight-semibold mb-1"
                htmlFor="maxPrice"
              >
                MAX PRICE:
              </label>

              <Controller
                name="maxPrice"
                control={control}
                rules={{
                  validate: (value) => {
                    const numericValue = value;
                    const tempMinPrice = minPrice;
                    return (tempMinPrice || tempMinPrice === 0) &&
                      numericValue !== 0 &&
                      !numericValue
                      ? "Max Price is required"
                      : (!numericValue && numericValue !== 0) ||
                      (!tempMinPrice && tempMinPrice !== 0) ||
                      (numericValue && numericValue > tempMinPrice) ||
                      (numericValue === 0 && tempMinPrice === 0) ||
                      "Max Price must be greater than Min Price";
                  },
                }}
                render={({ onChange, value, name }) => (
                  <TextInput
                    type="number"
                    placeholder="Max Price"
                    onChange={onChange}
                    name={name}
                    default={value}
                    gap="no"
                    clearFeildValue={true}
                  />
                )}
              />
              {errors.maxPrice && (
                <ErrorMessage>{errors.maxPrice.message}</ErrorMessage>
              )}
            </div>
          </div>
          <div className="form-group font-weight-semibold mb-1">
            <label
              className="font-md font-weight-semibold mb-1"
              htmlFor=""
            >
              Area Offered(Sq. Ft.)
            </label>
          </div>
          <div className="row gutter-8">
          <div className="col-6 form-group font-weight-semibold textInput-component-style mb-4">
            <label
              className="font-sm text-uppercase"
              htmlFor="minPrice"
            >
              Min Area:
            </label>
            <Controller
              name="minArea"
              control={control}
              rules={{
                validate: (value) => {
                  const numericMaxArea = maxArea;
                  if (
                    numericMaxArea &&
                    (value === undefined || value === null || value === "")
                  ) {
                    return "Min Area is required";
                  }
                  return undefined;
                },
              }}
              render={({ onChange, value, name }) => (
                <TextInput
                  type="number"
                  placeholder="Min Area"
                  onChange={onChange}
                  name={name}
                  default={value}
                  gap="no"
                  clearFeildValue={true}
                />
              )}
            />
            {errors.minArea && (
              <ErrorMessage>{errors.minArea.message}</ErrorMessage>
            )}
          </div>

          {/* <div className="col-auto d-flex align-items-center font-xxl text-gray-400 px-0 pt-2">
          -
        </div> */}

          <div className="col-6 form-group font-weight-semibold textInput-component-style mb-4">
            <label
              className="font-sm text-uppercase"
              htmlFor="minPrice"
            >
              Max Area:
            </label>
            <Controller
              name="maxArea"
              control={control}
              rules={{
                // required: "Max Price is required",
                validate: (value) => {
                  const numericValue = value;
                  const tempMinArea = minArea;
                  return (tempMinArea || tempMinArea === 0) &&
                    numericValue !== 0 &&
                    !numericValue
                    ? "Max Area is required"
                    : (!numericValue && numericValue !== 0) ||
                    (!tempMinArea && tempMinArea !== 0) ||
                    (numericValue && numericValue > tempMinArea) ||
                    (numericValue === 0 && tempMinArea === 0) ||
                    "Max Area must be greater than Min Area";
                },
              }}
              render={({ onChange, value, name }) => (
                <TextInput
                  type="number"
                  placeholder="Max Area"
                  onChange={onChange}
                  name={name}
                  default={value}
                  gap="no"
                  clearFeildValue={true}
                />
              )}
            />
            {errors.maxArea && (
              <ErrorMessage>{errors.maxArea.message}</ErrorMessage>
            )}
          </div>
          </div>
          {/* <div className="form-group font-weight-semibold textInput-component-style mb-4">
            <label
              className="font-sm text-uppercase"
              htmlFor="weightage"
            >
              Weight:
            </label>
            <Controller
              name="weightage"
              control={control}
              render={({ onChange, value, name }) => (
                <TextInput
                  type="number"
                  placeholder="Weight"
                  onChange={onChange}
                  name={name}
                  default={value}
                  gap="no"
                  clearFeildValue={true}
                  step='any'
                  maxValue={'100'}
                />
              )}
            />
            {errors.weightage && (
              <ErrorMessage>Weight is required</ErrorMessage>
            )}
          </div> */}

          {/* <div className="form-group font-weight-semibold mb-4">
            <label htmlFor="" className="font-sm text-uppercase">
              Floor Preferences
            </label>
            <Controller
              name="floorPreferences"
              control={control}
              // rules={{
              //   required: {
              //     value: true,
              //     message: "Floor Preferences is required",
              //   },
              //   validate: (value) => {
              //     return !!(value.length > 0);
              //   },
              // }}
              render={({ onChange, value }) => (
                <Multiselect
                  value={value}
                  data={floorPreferences}
                  textField={"name"}
                  valueField={"value"}
                  onChange={(val) => {
                    onChange(val);
                  }}
                  filter="startsWith"
                  messages={
                    floorPreferences?.length === 0
                      ? { emptyList: "No data found" }
                      : {}
                  }
                  placeholder="Select Floor"
                />
              )}
            />
            {errors.floorPreferences && (
              <ErrorMessage>Floor Preferences is required</ErrorMessage>
            )}
          </div> */}

          {/* <div className="form-group font-weight-semibold mb-4">
            <label
              className="font-sm font-weight-semibold mb-1"
              htmlFor="preferedProjects"
            >
              Preferred Projects:
            </label>
            <Controller
              name="preferedProjects"
              control={control}
              // rules={{
              //   required: {
              //     value: true,
              //     message: "Preferred Projects is required",
              //   },
              //   validate: (value) => {
              //     return !!(value.length > 0);
              //   },
              // }}
              render={({ onChange, value }) => (
                <Multiselect
                  value={value}
                  data={preferredProject}
                  textField={"name"}
                  valueField={"value"}
                  onChange={(val) => {
                    onChange(val);
                  }}
                  filter="startsWith"
                  messages={
                    preferredProject?.length === 0
                      ? { emptyList: "No data found" }
                      : {}
                  }
                  placeholder="Select Projects"
                />
              )}
            />
            {errors.preferedProjects && (
              <ErrorMessage>Preferred Projects is required</ErrorMessage>
            )}
          </div> */}

          {/* <div className="form-group font-weight-semibold mb-4">
            <label
              className="font-sm font-weight-semibold mb-1"
              htmlFor="preferredDevelopers"
            >
              Preferred Developers:
            </label>
            <Controller
              name="preferredDevelopers"
              control={control}
              // rules={{
              //   required: {
              //     value: true,
              //     message: "Preferred Developers is required",
              //   },
              //   validate: (value) => {
              //     return !!(value.length > 0);
              //   },
              // }}
              render={({ onChange, value }) => (
                <Multiselect
                  value={value}
                  data={preferredDevelopers}
                  textField={"name"}
                  valueField={"value"}
                  onChange={(val) => {
                    onChange(val);
                  }}
                  filter="startsWith"
                  messages={
                    preferredDevelopers?.length === 0
                      ? { emptyList: "No data found" }
                      : {}
                  }
                  placeholder="Select Developers"
                />
              )}
            />
            {errors.preferredDevelopers && (
              <ErrorMessage>Preferred Developers is required</ErrorMessage>
            )}
          </div> */}

          <div className="form-group font-weight-semibold mb-4">
            <label
              className="font-sm font-weight-semibold mb-1"
              htmlFor="preferredDevelopers"
            >
              REMARKS/NOTES:
            </label>
            <Controller
              control={control}
              name="note"
              // rules={{ required: true }}
              render={({ onChange, value, name }) => (
                <textarea
                  className="form-control rounded-sm font-weight-semibold h-auto"
                  placeholder="Comments"
                  name={name}
                  value={value}
                  onChange={onChange}
                  id="note"
                  rows={6}
                />
              )}
            />
            {errors.note && (
              <ErrorMessage>Remarks/Notes is required</ErrorMessage>
            )}
          </div>

          <div className="form-group font-weight-semibold mb-4">
            <label htmlFor="" className="font-sm text-uppercase">
              Lead Source<span className="text-danger">*</span>
            </label>
            <Controller
              control={control}
              rules={{ required: true }}
              name="leadSource"
              defaultValue={
                masterData?.data?.leadSources?.length === 1
                  ? masterData?.data?.leadSources?.[0]._id
                  : undefined
              }
              render={({ onChange, value }) => (
                <DropdownList
                  value={value}
                  defaultValue={value}
                  data={masterData?.data?.leadSources || []}
                  textField={"name"}
                  valueField={"_id"}
                  placeholder={"Select Lead Source"}
                  onChange={onChange}
                />
              )}
            />
            {errors.leadSource && (
              <ErrorMessage>Lead source is required</ErrorMessage>
            )}
          </div>

          <div className="mb-2 mt-2">
            {leadError && (
              <span className="message_validation">{leadError}</span>
            )}
          </div>
          <div className="form-group font-weight-semibold mb-3 text-right pt-3">
            <button
              disabled={updating}
              className="btn btn-primary font-weight-semibold font-normal px-4 "
            >
              Submit
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default LeadCreate;
